import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { seriesApi, type SeriesMediaScan, type SeriesMediaImportResult, type SeriesImportNumbering } from '../../lib/api.js'
import { mediaBrowserApi, type MediaFile } from '../../lib/mediaBrowserApi.js'
import type { Series } from '../../types/domain.js'
import { formatDuration } from '../../lib/utils.js'
import { MediaFileRow } from './MediaFileRow.js'
import { ModalShell } from '../common/ModalShell.js'
import { Spinner } from '../common/Spinner.js'
import './ProgramsView.css'

// ─── Props ────────────────────────────────────────────────────────────────────

interface SeriesBulkImportModalProps {
  series:     Series
  onClose:    () => void
  /** Appelé après un import ayant créé ou complété au moins une édition. */
  onImported: () => void
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  const [year, month, day] = iso.split('-')
  return `${day}/${month}/${year}`
}

const SKIP_LABELS: Record<string, string> = {
  NO_CODE_MATCH:  'code absent du nom',
  INVALID_DATE:   'date invalide',
  DUPLICATE_PART: 'partie en double',
}

// ─── Composant ────────────────────────────────────────────────────────────────

export const SeriesBulkImportModal = memo(function SeriesBulkImportModal({
  series, onClose, onImported,
}: SeriesBulkImportModalProps) {
  const [basePath,    setBasePath]    = useState('')
  const [currentPath, setCurrentPath] = useState('')
  const [folders,     setFolders]     = useState<MediaFile[]>([])
  const [scan,        setScan]        = useState<SeriesMediaScan | null>(null)
  const [loading,     setLoading]     = useState(true)
  const [error,       setError]       = useState('')
  const [selected,    setSelected]    = useState<Set<string>>(new Set())
  const [importing,   setImporting]   = useState(false)
  const [result,      setResult]      = useState<SeriesMediaImportResult | null>(null)
  const [showIgnored, setShowIgnored] = useState(false)
  const [search,      setSearch]      = useState('')
  // null = navigation par dossier ; tableau = résultats de recherche globale
  const [searchResults, setSearchResults] = useState<MediaFile[] | null>(null)
  const [searching,   setSearching]   = useState(false)
  // Numérotation de la sélection, redécidée à chaque import. Les champs sont
  // pré-remplis avec la suite de ce qui existe, puis restent libres.
  // Sur une série déclarée sans saison ni épisode, la question ne se pose pas.
  const [numberEpisodes, setNumberEpisodes] = useState(series.numberEpisodes)
  const [season,       setSeason]       = useState('1')
  const [firstEpisode, setFirstEpisode] = useState('1')
  // Quand les noms de fichiers portent déjà leur S/E, c'est eux qui font foi :
  // le rang de sélection dépend de l'ordre des fichiers, pas de l'intention.
  const [useFileNumbering, setUseFileNumbering] = useState(true)
  // Dates saisies par l'opérateur pour les candidats sans date, par clé de groupe.
  const [prefixDateInputs, setPrefixDateInputs] = useState<Record<string, string>>({})
  // Un candidat sans date est importé « inédit » (mode ID) par défaut ; l'opérateur
  // peut basculer une entrée vers une date connue s'il en a une.
  const [dateOverrideMode, setDateOverrideMode] = useState<Record<string, boolean>>({})

  const baseCode = scan?.baseCode ?? null

  /** Clé unique d'un candidat pour la sélection. */
  const candidateKey = useCallback((c: { date: string | null; groupKey: string }) => {
    return c.date ?? c.groupKey
  }, [])

  // Navigation + détection : le dossier courant est analysé à chaque changement.
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    setFolders([])
    setScan(null)
    Promise.all([
      mediaBrowserApi.list(currentPath),
      seriesApi.scanMedia(series.id, currentPath),
    ])
      .then(([listing, scanned]) => {
        if (cancelled) return
        setBasePath(listing.path)
        setFolders(listing.files.filter(file => file.type === 'directory'))
        setScan(scanned)
        setNumberEpisodes(scanned.numberEpisodes)
        setSeason(String(scanned.suggestedNumbering.season))
        setFirstEpisode(String(scanned.suggestedNumbering.firstEpisode))
        // Par défaut, seuls les fichiers nouveaux sont cochés : ni une édition
        // déjà créée à cette date, ni un fichier déjà rattaché à une fiche. Les
        // candidats sans date sont importés inédits (mode ID), sans date fictive.
        setSelected(new Set(
          scanned.candidates
            .filter(c => !c.existingProgramId && !c.importedProgramId)
            .map(c => candidateKey(c)),
        ))
      })
      .catch(reason => {
        if (cancelled) return
        setError(reason?.message ?? 'Erreur de chargement du dossier')
        setFolders([])
        setScan(null)
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [series.id, currentPath])

  // Les fichiers nouveaux d'abord : c'est eux qu'on vient importer, les déjà
  // importés suivent pour pouvoir être recochés. L'ordre d'import reste décidé
  // côté serveur (date, puis nom de fichier).
  const candidates = useMemo(() => {
    const all = scan?.candidates ?? []
    return [...all.filter(c => !c.importedProgramId), ...all.filter(c => c.importedProgramId)]
  }, [scan])
  const importedCount = useMemo(() => candidates.filter(c => c.importedProgramId).length, [candidates])
  const selectable = candidates.length
  const allSelected = selectable > 0 && selected.size === selectable

  const toggle = useCallback((key: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const toggleAll = useCallback(() => {
    setSelected(prev => prev.size === candidates.length
      ? new Set()
      : new Set(candidates.map(c => candidateKey(c))))
  }, [candidates, candidateKey])

  // Recherche globale débouncée — identique au navigateur de médias.
  useEffect(() => {
    const query = search.trim()
    if (!query) {
      setSearchResults(null)
      setSearching(false)
      return
    }
    setSearching(true)
    const timer = setTimeout(() => {
      mediaBrowserApi.list('', query)
        .then(listing => setSearchResults(listing.files))
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false))
    }, 350)
    return () => clearTimeout(timer)
  }, [search])

  const goBack = useCallback(() => {
    if (searchResults) {
      setSearch('')
      return
    }
    setCurrentPath(path => path.split('/').slice(0, -1).join('/'))
  }, [searchResults])

  /** Un résultat de recherche ouvre le dossier qui contient le fichier. */
  const openContainingFolder = useCallback((file: MediaFile) => {
    const folder = file.type === 'directory'
      ? file.relativePath
      : file.relativePath.split('/').slice(0, -1).join('/')
    setSearch('')
    setCurrentPath(folder)
  }, [])

  /** Bascule un candidat sans date entre « inédit » (mode ID) et date connue saisie. */
  const toggleDateOverride = useCallback((groupKey: string) => {
    setDateOverrideMode(prev => {
      const turningOn = !prev[groupKey]
      if (!turningOn) {
        setPrefixDateInputs(inputs => {
          if (!(groupKey in inputs)) return inputs
          const next = { ...inputs }
          delete next[groupKey]
          return next
        })
      }
      return { ...prev, [groupKey]: turningOn }
    })
  }, [])

  const newCount = useMemo(
    () => candidates.filter(c => selected.has(candidateKey(c)) && !c.existingProgramId && !c.importedProgramId).length,
    [candidates, selected, candidateKey],
  )

  const seasonValue       = Math.max(1, Math.round(Number(season)       || 1))
  const firstEpisodeValue = Math.max(1, Math.round(Number(firstEpisode) || 1))

  const selectedCandidates = useMemo(
    () => candidates.filter(c => selected.has(candidateKey(c))),
    [candidates, selected, candidateKey],
  )

  /** Combien des candidats sélectionnés portent leur numéro dans leur nom. */
  const fileNumbered = useMemo(
    () => selectedCandidates.filter(c => c.episode != null).length,
    [selectedCandidates],
  )
  /** La reprise depuis les noms n'est proposée que si le dossier s'y prête. */
  const fileNumberingAvailable = candidates.some(c => c.episode != null)
  const fileNumberingActive = numberEpisodes && fileNumberingAvailable && useFileNumbering

  const numbering = useMemo((): SeriesImportNumbering => {
    if (!numberEpisodes) return null
    if (fileNumberingActive) return { mode: 'FILENAME', fallbackSeason: seasonValue }
    return { mode: 'SEQUENCE', season: seasonValue, firstEpisode: firstEpisodeValue }
  }, [numberEpisodes, fileNumberingActive, seasonValue, firstEpisodeValue])

  /**
   * Aperçu de ce que la sélection va produire. En mode séquentiel les numéros
   * suivent l'ordre de la liste, pas l'ordre de cochage : l'opérateur doit
   * pouvoir le vérifier avant de lancer un import qui décale des épisodes
   * existants. En mode nom de fichier, l'aperçu montre les bornes réellement
   * lues et signale les fichiers qu'aucun numéro ne couvre.
   */
  const numberingPreview = useMemo(() => {
    if (!numbering || selectedCandidates.length === 0) return null
    const count = selectedCandidates.length
    const label = (s: number, e: number) => `S${s}E${String(e).padStart(2, '0')}`

    if (numbering.mode === 'FILENAME') {
      const numbered = selectedCandidates
        .filter(c => c.episode != null)
        .map(c => ({ season: c.season ?? numbering.fallbackSeason, episode: c.episode! }))
        .sort((a, b) => a.season - b.season || a.episode - b.episode)
      if (numbered.length === 0) return null
      return {
        mode: 'FILENAME' as const,
        count: numbered.length,
        unnumbered: count - numbered.length,
        from: label(numbered[0].season, numbered[0].episode),
        to:   label(numbered[numbered.length - 1].season, numbered[numbered.length - 1].episode),
      }
    }
    return {
      mode: 'SEQUENCE' as const,
      count,
      unnumbered: 0,
      from: label(numbering.season, numbering.firstEpisode),
      to:   label(numbering.season, numbering.firstEpisode + count - 1),
    }
  }, [numbering, selectedCandidates])

  const handleImport = useCallback(async () => {
    if (selected.size === 0) return
    setImporting(true)
    setError('')
    try {
      // Séparer les candidats datés, ceux avec une date saisie, et les inédits (mode ID).
      const dates = selectedCandidates.filter(c => c.date).map(c => c.date!)
      const prefixDates: Record<string, string> = {}
      const idGroupKeys: string[] = []
      for (const c of selectedCandidates) {
        if (!c.prefix) continue
        if (dateOverrideMode[c.groupKey]) {
          const assignedDate = prefixDateInputs[c.groupKey]
          if (!assignedDate || !/^\d{4}-\d{2}-\d{2}$/.test(assignedDate)) {
            setError(`Veuillez saisir une date valide pour le fichier ${c.parts.map(p => p.fileName).join(', ')}`)
            setImporting(false)
            return
          }
          prefixDates[c.groupKey] = assignedDate
        } else {
          idGroupKeys.push(c.groupKey)
        }
      }
      const imported = await seriesApi.importMedia(
        series.id, currentPath, dates, numbering,
        Object.keys(prefixDates).length > 0 ? prefixDates : null,
        idGroupKeys.length > 0 ? idGroupKeys : null,
      )
      setResult(imported)
      if (imported.created.length > 0 || imported.reused.length > 0) onImported()
    } catch (reason: any) {
      setError(reason?.message ?? "Échec de l'import")
    } finally {
      setImporting(false)
    }
  }, [selectedCandidates, selected, series.id, currentPath, onImported, numbering, prefixDateInputs, dateOverrideMode])

  // ── Écran de compte rendu ───────────────────────────────────────────────────
  if (result) {
    return (
      <ModalShell title="Import terminé" size="wide" onClose={onClose} className="catalog-bulk-import">
        <div className="catalog-bulk-import__report">
          <div className="catalog-bulk-import__report-line">
            <strong>{result.created.length}</strong> édition{result.created.length > 1 ? 's' : ''} créée{result.created.length > 1 ? 's' : ''}
            {result.reused.length > 0 && <> · <strong>{result.reused.length}</strong> déjà existante{result.reused.length > 1 ? 's' : ''} complétée{result.reused.length > 1 ? 's' : ''}</>}
          </div>
          {[...result.created, ...result.reused].map(entry => (
            <div key={entry.programId} className="catalog-bulk-import__report-row">
              <span className="catalog-bulk-import__date">{entry.date ? formatDate(entry.date) : 'Inédit'}</span>
              <span className="catalog-bulk-import__title">{entry.title}</span>
              <span className="catalog-bulk-import__meta">{entry.linkedFiles} média{entry.linkedFiles > 1 ? 's' : ''} associé{entry.linkedFiles > 1 ? 's' : ''}</span>
            </div>
          ))}
          {result.warnings.map((warning, index) => (
            <div key={`w${index}`} className="tp-feedback tp-feedback--warning" role="status">
              {warning.date ? formatDate(warning.date) : 'Inédit'} — {warning.message}
            </div>
          ))}
          {result.failed.map((failure, index) => (
            <div key={`f${index}`} className="tp-feedback tp-feedback--error" role="alert">
              {failure.date ? formatDate(failure.date) : 'Inédit'} — {failure.reason}
            </div>
          ))}
        </div>
        <div className="tp-modal-actions">
          <button type="button" className="tp-button tp-button--info" onClick={onClose}>Fermer</button>
        </div>
      </ModalShell>
    )
  }

  // ── Écran de détection ──────────────────────────────────────────────────────
  return (
    <ModalShell
      title={`Import en bloc — ${series.title}`}
      size="wide"
      onClose={onClose}
      className="catalog-bulk-import"
    >
      <div className="catalog-media-browser__path">
        {basePath}{currentPath ? ` / ${currentPath}` : ''}
      </div>

      <div className="catalog-bulk-import__hint">
        {baseCode
          ? <>Fichiers reconnus : le code <code>{baseCode}</code> présent n'importe où dans le nom (date <code>AAAAMMJJ</code> ou préfixe libre autour) — suffixes <code>_P1</code>/<code>_P2</code> regroupés en multi-parties, paramètres repris de la série.</>
          : <>Cette série n'a pas de code programme : renseignez-le dans sa fiche pour activer la détection.</>}
      </div>

      {/* À quoi correspond la sélection — redécidé à chaque import. */}
      {!loading && !error && baseCode && (scan?.numberEpisodes ?? series.numberEpisodes) && (
        <fieldset className="catalog-bulk-import__numbering">
          <legend>À quoi correspond cette sélection ?</legend>
          <label className="catalog-bulk-import__numbering-toggle">
            <input
              type="checkbox"
              checked={numberEpisodes}
              onChange={e => setNumberEpisodes(e.target.checked)}
            />
            <span>Numéroter les éditions importées</span>
          </label>
          {numberEpisodes && fileNumberingAvailable && (
            <label className="catalog-bulk-import__numbering-toggle">
              <input
                type="checkbox"
                checked={useFileNumbering}
                onChange={e => setUseFileNumbering(e.target.checked)}
              />
              <span>Reprendre la numérotation des noms de fichiers</span>
            </label>
          )}
          {numberEpisodes && (
            <div className="catalog-bulk-import__numbering-fields">
              <label className="tp-form-field">
                <span className="tp-field-label">
                  {fileNumberingActive ? 'Saison par défaut' : 'Saison'}
                </span>
                <input
                  type="number" min="1" step="1" className="tp-input"
                  value={season} onChange={e => setSeason(e.target.value)}
                />
              </label>
              {!fileNumberingActive && (
                <label className="tp-form-field">
                  <span className="tp-field-label">Premier épisode</span>
                  <input
                    type="number" min="1" step="1" className="tp-input"
                    value={firstEpisode} onChange={e => setFirstEpisode(e.target.value)}
                  />
                </label>
              )}
            </div>
          )}
          <p className="catalog-bulk-import__numbering-note">
            {numberingPreview
              ? numberingPreview.mode === 'FILENAME'
                ? <>
                    {numberingPreview.count} édition{numberingPreview.count > 1 ? 's' : ''} prendront le numéro lu dans leur nom de fichier,
                    {' '}de <strong>{numberingPreview.from}</strong>
                    {numberingPreview.count > 1 && <> à <strong>{numberingPreview.to}</strong></>}.
                    {' '}Aucun épisode existant n'est repoussé.
                    {numberingPreview.unnumbered > 0 && <>
                      {' '}<strong>{numberingPreview.unnumbered} fichier{numberingPreview.unnumbered > 1 ? 's' : ''}</strong> sans numéro
                      {' '}dans le nom resteront sans saison ni épisode.
                    </>}
                  </>
                : <>
                    Les {numberingPreview.count} édition{numberingPreview.count > 1 ? 's' : ''} sélectionnée{numberingPreview.count > 1 ? 's' : ''} recevront&nbsp;
                    <strong>{numberingPreview.from}</strong>
                    {numberingPreview.count > 1 && <> à <strong>{numberingPreview.to}</strong></>}, dans l'ordre des dates.
                    {' '}Les épisodes déjà numérotés à partir de ce rang dans la saison {seasonValue} seront repoussés d'autant.
                  </>
              : numberEpisodes
                ? "Cochez des dates pour voir les numéros qui leur seront attribués."
                : "Les éditions importées resteront sans saison ni épisode."}
          </p>
        </fieldset>
      )}

      {/* Navigation dans les dossiers — même barre que le navigateur de médias */}
      <div className="catalog-media-browser__toolbar">
        {(currentPath || searchResults) && (
          <button
            type="button"
            onClick={goBack}
            className="tp-icon-button catalog-media-browser__back"
            aria-label={searchResults ? 'Quitter la recherche' : 'Retour au dossier parent'}
            title={searchResults ? 'Quitter la recherche' : 'Retour au dossier parent'}
          >
            {searchResults ? '✕' : '↑'}
          </button>
        )}
        <input
          type="text"
          placeholder="Rechercher dans tous les dossiers…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="tp-input"
          aria-label="Rechercher un fichier pour ouvrir son dossier"
        />
      </div>

      {/* Dossiers puis détections */}
      <div className="catalog-media-browser__list">

        {/* Mode recherche globale : ouvrir le dossier contenant le fichier trouvé */}
        {searchResults !== null && (
          searching
            ? <div className="tp-state catalog-media-browser__state" aria-live="polite">Recherche…</div>
            : searchResults.length === 0
              ? <div className="tp-state catalog-media-browser__state is-empty">Aucun élément ne correspond à la recherche</div>
              : searchResults.map(file => (
                <MediaFileRow
                  key={file.path}
                  file={file}
                  showPath
                  selected={false}
                  title="Ouvrir le dossier contenant ce fichier"
                  onClick={() => openContainingFolder(file)}
                  onDoubleClick={() => openContainingFolder(file)}
                />
              ))
        )}

        {/* Navigation par dossier */}
        {searchResults === null && !error && folders.map(folder => (
          <MediaFileRow
            key={folder.path}
            file={folder}
            selected={false}
            title={`Ouvrir ${folder.name}`}
            onClick={() => setCurrentPath(folder.relativePath)}
            onDoubleClick={() => setCurrentPath(folder.relativePath)}
          />
        ))}

        {searchResults === null && loading && (
          <div className="tp-state catalog-media-browser__state" aria-live="polite"><Spinner /> Analyse du dossier…</div>
        )}
        {searchResults === null && !loading && error && (
          <div className="tp-state catalog-media-browser__state tp-feedback--error" role="alert">⚠ {error}</div>
        )}
        {searchResults === null && !loading && !error && candidates.length === 0 && (
          <div className="tp-state catalog-media-browser__state is-empty">
            {baseCode
              ? `Aucun fichier contenant le code ${baseCode} dans ce dossier`
              : 'Détection impossible sans code programme'}
          </div>
        )}
        {searchResults === null && !loading && !error && candidates.length > 0 && (
          <>
            {importedCount > 0 && (
              <div className="catalog-bulk-import__auto-dates">
                <span className="catalog-bulk-import__meta">
                  {candidates.length - importedCount} fichier{candidates.length - importedCount > 1 ? 's' : ''} nouveau{candidates.length - importedCount > 1 ? 'x' : ''} · {importedCount} déjà importé{importedCount > 1 ? 's' : ''}, décoché{importedCount > 1 ? 's' : ''} par défaut. Recocher un fichier déjà importé le réassocie à sa fiche, sans en créer une seconde.
                </span>
              </div>
            )}
            {candidates.some(c => c.prefix !== null && !c.importedProgramId) && (
              <div className="catalog-bulk-import__auto-dates">
                <span className="catalog-bulk-import__meta">
                  Les fichiers sans date sont importés « inédits » (sans date de diffusion connue) : ils resteront non diffusés tant qu'ils ne seront pas programmés sur la grille. Saisissez une date pour un fichier si elle est déjà connue.
                </span>
              </div>
            )}
            <div className="catalog-bulk-import__row catalog-bulk-import__row--head">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                aria-label="Tout sélectionner"
              />
              <span className="catalog-bulk-import__date">Date</span>
              <span className="catalog-bulk-import__title">Fichiers</span>
              <span className="catalog-bulk-import__meta">Durée</span>
              <span className="catalog-bulk-import__status">État</span>
            </div>
            {candidates.map(candidate => {
              const key = candidateKey(candidate)
              const isPrefix = candidate.prefix !== null
              const imported = candidate.importedProgramId !== null
              return (
              <label
                key={key}
                className={`catalog-bulk-import__row${selected.has(key) ? ' is-selected' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={selected.has(key)}
                  onChange={() => toggle(key)}
                />
                <span className="catalog-bulk-import__date">
                  {!isPrefix ? formatDate(candidate.date!) : imported ? (
                    // La fiche existe déjà : sa date n'est pas à choisir ici.
                    <span className="catalog-bulk-import__meta">—</span>
                  ) : (
                    <div className="catalog-bulk-import__date-cell">
                      {dateOverrideMode[candidate.groupKey] ? (
                        <input
                          type="date"
                          className="tp-input tp-input--compact"
                          value={prefixDateInputs[candidate.groupKey] ?? ''}
                          onChange={e => setPrefixDateInputs(prev => ({ ...prev, [candidate.groupKey]: e.target.value }))}
                          title="Date de diffusion prévue"
                          aria-label={`Date de diffusion pour ${candidate.parts[0]?.fileName ?? candidate.prefix}`}
                        />
                      ) : (
                        <span className="catalog-bulk-import__badge catalog-bulk-import__badge--id" title="Édition inédite, sans date de diffusion connue">{candidate.titleHint ?? candidate.prefix}</span>
                      )}
                      <button
                        type="button"
                        className="catalog-bulk-import__date-toggle"
                        onClick={() => toggleDateOverride(candidate.groupKey)}
                        title={dateOverrideMode[candidate.groupKey] ? 'Revenir à inédit' : 'Assigner une date connue'}
                        aria-label={dateOverrideMode[candidate.groupKey] ? 'Revenir à inédit' : 'Assigner une date connue'}
                      >
                        {dateOverrideMode[candidate.groupKey] ? '↺' : '📅'}
                      </button>
                    </div>
                  )}
                </span>
                <span className="catalog-bulk-import__title">
                  {candidate.parts.map(part => part.fileName).join(' · ')}
                  {candidate.parts.length > 1 && (
                    <span className="catalog-bulk-import__parts">{candidate.parts.length} parties</span>
                  )}
                  {fileNumberingActive && candidate.episode != null && (
                    <span className="catalog-bulk-import__parts" title="Numérotation lue dans le nom de fichier">
                      S{candidate.season ?? seasonValue}E{String(candidate.episode).padStart(2, '0')}
                    </span>
                  )}
                </span>
                <span className="catalog-bulk-import__meta">
                  {candidate.totalDurationSec != null ? formatDuration(candidate.totalDurationSec) : '—'}
                </span>
                <span className="catalog-bulk-import__status">
                  {imported
                    ? (
                      <span
                        className="catalog-bulk-import__badge catalog-bulk-import__badge--imported"
                        title={`Déjà rattaché à « ${candidate.importedTitle ?? ''} »${candidate.importedArchived ? ' (archivée)' : ''} : le recocher réassocie le fichier à cette fiche`}
                      >
                        {candidate.importedArchived ? 'Déjà importé · archivé' : 'Déjà importé'}
                      </span>
                    )
                    : candidate.existingProgramId
                      ? <span className="catalog-bulk-import__badge catalog-bulk-import__badge--existing" title="Une édition existe déjà à cette date : seuls les médias seront (re)associés">Déjà créée</span>
                      : <span className="catalog-bulk-import__badge catalog-bulk-import__badge--new">Nouvelle</span>}
                </span>
              </label>
              )
            })}
            {scan?.truncated && (
              <div className="tp-feedback tp-feedback--warning" role="status">
                Trop de fichiers dans ce dossier : la liste a été tronquée.
              </div>
            )}
            {(scan?.ignored.length ?? 0) > 0 && (
              <div className="catalog-bulk-import__ignored">
                <button
                  type="button"
                  className="catalog-bulk-import__ignored-toggle"
                  aria-expanded={showIgnored}
                  onClick={() => setShowIgnored(value => !value)}
                >
                  {showIgnored ? '▼' : '▶'} {scan!.ignored.length} fichier{scan!.ignored.length > 1 ? 's' : ''} ignoré{scan!.ignored.length > 1 ? 's' : ''}
                </button>
                {showIgnored && (
                  <ul className="catalog-bulk-import__ignored-list">
                    {scan!.ignored.map(item => (
                      <li key={item.fileName}>
                        {item.fileName} <span className="catalog-bulk-import__meta">({SKIP_LABELS[item.reason] ?? item.reason})</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <div className="catalog-media-browser__footer">
        <div className="catalog-media-browser__selection">
          <div className="catalog-media-browser__selection-name">
            {selected.size === 0
              ? 'Aucune édition sélectionnée'
              : `${selected.size} édition${selected.size > 1 ? 's' : ''} sélectionnée${selected.size > 1 ? 's' : ''}${newCount !== selected.size ? ` (${newCount} nouvelle${newCount > 1 ? 's' : ''})` : ''}`}
          </div>
        </div>
        <button type="button" onClick={onClose} className="tp-button" disabled={importing}>Annuler</button>
        <button
          type="button"
          onClick={handleImport}
          disabled={selected.size === 0 || importing || loading}
          className="tp-button tp-button--info"
          data-state={importing ? 'loading' : undefined}
        >
          {importing ? <><Spinner /> Import…</> : `Importer${selected.size > 0 ? ` (${selected.size})` : ''}`}
        </button>
      </div>
    </ModalShell>
  )
})
